import { Tooltip } from '@/global/components/ui/tooltip'
import {
    Card,
    CardContent,
    Grid,
    Typography,
    useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import dayjs from 'dayjs'
import { Link } from 'global/components/Link'
import { periodicidade } from 'locales/plan'
import { TriangleAlertIcon } from 'lucide-react'
import { usePlanosContext } from 'pages/Planos'
import { clientes } from 'service/api'

export default function GridDados() {
    const theme = useTheme()
    const matchUpSm = useMediaQuery(theme.breakpoints.up('sm'))

    const xs = matchUpSm ? 6 : 12

    return (
        <Grid container spacing={2}>
            <Grid item xs={xs}>
                <Card
                    variant="outlined"
                    sx={{
                        color: theme => theme.palette.grey[600],
                    }}
                >
                    <DadosContrato mobile={!matchUpSm} />
                </Card>
            </Grid>
            <Grid item xs={xs}>
                <Card
                    variant="outlined"
                    sx={{
                        color: theme => theme.palette.grey[600],
                    }}
                >
                    <DadosPagamento mobile={!matchUpSm} />
                </Card>
            </Grid>
        </Grid>
    )
}

function DadosContrato({ mobile }: { mobile?: boolean }) {
    const { assinatura, planos } = usePlanosContext()
    const meuPlano = planos?.find(
        it =>
            it.limiteFuncionarios ===
                Number(assinatura?.additionalInfo?.replace(/\D/g, '')) &&
            it.periodicity === assinatura?.periodicity,
    )

    const { data } = clientes.useFetchById<{
        ativos: number
        limite: number
    }>('limites')

    const { ativos, limite } = data || { ativos: 0, limite: 0 },
        isBloqueado = ativos > limite,
        isMax = ativos === limite

    return (
        <CardContent
            sx={{ height: mobile ? 'auto' : '13.5rem', overflow: 'auto' }}
        >
            <Typography
                variant="h6"
                color="text.secondary"
                sx={{ textTransform: 'uppercase' }}
            >
                Dados do contrato
            </Typography>
            <Typography
                variant="body1"
                color="text.secondary"
                sx={{ display: 'inline-flex' }}
            >
                Funcionários cadastrados:{' '}
                <Typography
                    variant="body1"
                    color={(() => {
                        if (isBloqueado) return 'error'
                        if (isMax) return 'primary'
                        return 'text.secondary'
                    })()}
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                    }}
                >
                    &nbsp;{ativos}/{limite}&nbsp;
                    {isBloqueado && (
                        <Tooltip
                            title={
                                <span>
                                    Você ultrapassou o limite de funcionários
                                    ativos.
                                    <br />
                                    <br />
                                    Altere seu plano ou exclua funcionários para
                                    continuar usando o sistema.
                                    <br />
                                    Depois faça login novamente para que o
                                    sistema seja liberado.
                                </span>
                            }
                        >
                            <TriangleAlertIcon className="text-red-500" />
                        </Tooltip>
                    )}
                    {isMax && (
                        <Tooltip
                            title={`Você atingiu o limite de funcionários ativos. 
                                Para cadastrar mais funcionários, altere seu plano.`}
                        >
                            <TriangleAlertIcon className="text-yellow-500" />
                        </Tooltip>
                    )}
                </Typography>
            </Typography>
            {!!assinatura && (
                <>
                    <Typography variant="body1" color="text.secondary">
                        Plano: {(() => {
                            if (!meuPlano) return 'Plano não encontrado'
                            if (meuPlano.limiteFuncionarios == 0)
                                return 'Especial'
                            return `até ${meuPlano.limiteFuncionarios} funcionários`
                        })()}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Periodicidade: {periodicidade[assinatura?.periodicity]}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Data da 1ª cobrança:{' '}
                        {dayjs(assinatura?.firstPayDayDate).format(
                            'DD/MM/YYYY',
                        )}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Qtde de Transações:{' '}
                        {assinatura?.quantity === 0 ? (
                            <Tooltip
                                title={
                                    <span>
                                        Faltando 30 dias para o vencimento, a
                                        <br />
                                        próxima transação é adicionada
                                        <br />
                                        automaticamente no sistema. O sistema só
                                        <br />
                                        para de lançar automaticamente quando o
                                        <br />
                                        contrato é cancelado manualmente.
                                        <br />
                                    </span>
                                }
                            >
                                <Link>Indeterminado</Link>
                            </Tooltip>
                        ) : (
                            assinatura?.quantity
                        )}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Email financeiro: {assinatura?.Customer?.emails?.at(-1)}
                    </Typography>
                </>
            )}
        </CardContent>
    )
}

function DadosPagamento({ mobile }: { mobile?: boolean }) {
    const { assinatura } = usePlanosContext()

    if (!assinatura)
        return <CardContent sx={{ height: mobile ? 'auto' : '13.5rem' }} />

    return (
        <CardContent
            sx={{ height: mobile ? 'auto' : '13.5rem', oveflow: 'auto' }}
        >
            {assinatura?.PaymentMethodBoleto && (
                <>
                    <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase' }}
                    >
                        Dados do boleto
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Multa por atraso:{' '}
                        {(assinatura?.PaymentMethodBoleto?.fine! / 100).toFixed(
                            2,
                        )}
                        %
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Juros ao mês:{' '}
                        {(
                            assinatura?.PaymentMethodBoleto?.interest! / 100
                        ).toFixed(2)}
                        %
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Prazo máximo emissão de 2º via:{' '}
                        {assinatura?.PaymentMethodBoleto?.deadlineDays} dias
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Valor das próximas transações a serem geradas:{' '}
                        {(assinatura?.value! / 100).toLocaleString('pt-br', {
                            style: 'currency',
                            currency: 'BRL',
                        })}
                    </Typography>
                </>
            )}
            {assinatura?.PaymentMethodCreditCard && (
                <>
                    <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase' }}
                    >
                        Dados do cartão
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Valor das próximas transações a serem geradas:{' '}
                        {(assinatura?.value! / 100).toLocaleString('pt-br', {
                            style: 'currency',
                            currency: 'BRL',
                        })}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Cartão das próximas transações a serem geradas:{' '}
                        <strong>
                            {
                                assinatura.PaymentMethodCreditCard.Card?.Brand
                                    ?.name
                            }{' '}
                            **** ****{' '}
                            {assinatura?.PaymentMethodCreditCard?.Card?.number?.slice(
                                -4,
                            )}
                        </strong>
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Multa por atraso: R$ 16,00
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Juros ao mês: R$ 8,00 (R$ 0,27 ao dia)
                    </Typography>
                </>
            )}
        </CardContent>
    )
}
